import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Typography,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { ProcessingConfig } from '../types';
import { PresetManager, SavedPreset } from '../utils/presetManager';

interface PresetManagerUIProps {
  currentConfig: ProcessingConfig;
  onLoadPreset: (config: ProcessingConfig) => void;
}

const PresetManagerUI: React.FC<PresetManagerUIProps> = ({ currentConfig, onLoadPreset }) => {
  const [presets, setPresets] = useState<SavedPreset[]>(PresetManager.getAllPresets());
  const [selectedPresetId, setSelectedPresetId] = useState<string>('');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [presetName, setPresetName] = useState('');

  const refreshPresets = () => {
    setPresets(PresetManager.getAllPresets());
  };

  const handleSavePreset = () => {
    if (presetName.trim()) {
      try {
        PresetManager.savePreset(presetName, currentConfig);
        refreshPresets();
        setSaveDialogOpen(false);
        setPresetName('');
      } catch (error) {
        alert('Failed to save preset');
      }
    }
  };

  const handleLoadPreset = (id: string) => {
    const preset = PresetManager.getPreset(id);
    if (preset) {
      onLoadPreset(preset.config);
      setSelectedPresetId(id);
    }
  };

  const handleDeletePreset = (id: string) => {
    if (window.confirm('Are you sure you want to delete this preset?')) {
      try {
        PresetManager.deletePreset(id);
        refreshPresets();
        if (selectedPresetId === id) {
          setSelectedPresetId('');
        }
      } catch (error) {
        alert('Failed to delete preset');
      }
    }
  };

  const handleExportPresets = () => {
    const json = PresetManager.exportPresets();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ludwing-mastering-presets-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportPresets = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const json = e.target?.result as string;
          PresetManager.importPresets(json);
          refreshPresets();
          alert('Presets imported successfully!');
        } catch (error) {
          alert('Failed to import presets. Invalid file format.');
        }
      };
      reader.readAsText(file);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <FormControl fullWidth size="small">
          <InputLabel>Load Preset</InputLabel>
          <Select
            value={selectedPresetId}
            label="Load Preset"
            onChange={(e) => handleLoadPreset(e.target.value)}
          >
            <MenuItem value="">
              <em>Select a preset...</em>
            </MenuItem>
            {presets.map((preset) => (
              <MenuItem key={preset.id} value={preset.id}>
                {preset.name}
                {preset.isBuiltIn && (
                  <Chip label="Built-in" size="small" sx={{ ml: 1 }} />
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={<SaveIcon />}
          onClick={() => setSaveDialogOpen(true)}
          size="small"
          sx={{ minWidth: 'auto' }}
        >
          Save
        </Button>

        <Button
          variant="outlined"
          onClick={() => setManageDialogOpen(true)}
          size="small"
          sx={{ minWidth: 'auto' }}
        >
          Manage
        </Button>
      </Box>

      {/* Save Preset Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <DialogTitle>Save Current Settings as Preset</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Preset Name"
            fullWidth
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSavePreset();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSavePreset} variant="contained" disabled={!presetName.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Presets Dialog */}
      <Dialog open={manageDialogOpen} onClose={() => setManageDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Manage Presets</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
            <Button
              startIcon={<DownloadIcon />}
              onClick={handleExportPresets}
              variant="outlined"
              size="small"
            >
              Export All
            </Button>
            <Button
              startIcon={<UploadIcon />}
              component="label"
              variant="outlined"
              size="small"
            >
              Import
              <input
                type="file"
                hidden
                accept=".json"
                onChange={handleImportPresets}
              />
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Built-in Presets
          </Typography>
          <List dense>
            {presets
              .filter((p) => p.isBuiltIn)
              .map((preset) => (
                <ListItem key={preset.id}>
                  <ListItemText
                    primary={preset.name}
                    secondary={`Genre: ${preset.config.genre || 'Auto'} • ${preset.config.outputFormat.toUpperCase()}`}
                  />
                </ListItem>
              ))}
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Your Presets
          </Typography>
          {presets.filter((p) => !p.isBuiltIn).length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No custom presets saved yet.
            </Typography>
          ) : (
            <List dense>
              {presets
                .filter((p) => !p.isBuiltIn)
                .map((preset) => (
                  <ListItem key={preset.id}>
                    <ListItemText
                      primary={preset.name}
                      secondary={`Saved: ${preset.createdAt.toLocaleDateString()}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleDeletePreset(preset.id)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PresetManagerUI;
